import { useEffect, useMemo } from "react";
import "./ReelsData.css";
import { useLoadTypeContentData } from "../../../../Utils/customHooks/UpToDateContent/useLoadTypeContent";
import { getContentFeedDateWindowFromComponent } from "../../../../Screens/DashboardView/StayUpToDate/AddComponentPicker/contentFeedTimeframe";
import { StayFeedLoadingPlaceholder } from "../../../../Screens/DashboardView/MasterTemplateEditor/WebsiteLibFeedSkeleton";
import { debounce } from "lodash";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import track, {
  TRACKING_TYPES,
} from "../../../../Utils/SupabaseServices/Track";
import { isMobile } from "react-device-detect";
import {
  handleFirstImage,
  updateIsEmptyStatus,
} from "../../../../Utils/StayUpToDate/RenderComponent";

import { useDomLoaded } from "../../../../hooks/DomLoadedContext";
import PostTemplate from "../CommonComponents/PostTemplate";
import { normalizeFilterForType } from "../../../../Utils/helpers/carouselHelper";
import { FeedSectionEmptyState } from "../FeedSectionEmptyState";
import { websiteEditorFeedEmptyMessage } from "../../../../Screens/DashboardView/MasterTemplateEditor/websiteEditorFeedEmptyMessage";

const ReelsData = (props: any) => {
  const {
    user_id,
    username,
    handleRedirect,
    brand_color,
    title,
    component,
    removedPostIds,
    from,
    designTokens,
    templateGridLoaderProps,
    isLinks,
    showEmptyInEditor = false,
  } = props;
  const platform = "instagram";
  const type = "REELS";
  const itemsPerPage = 3;

  const contentDateWindow = useMemo(
    () => getContentFeedDateWindowFromComponent(component),
    [component?.details, component?.id]
  );

  const {
    isLoading: ReelsDataLoading,
    data: ReelsData,
    total_count,
  }: any = useLoadTypeContentData(
    username,
    user_id,
    platform,
    type,
    removedPostIds,
    component?.content_order,
    component?.custom_order?.split(","),
    component?.shoppable_content_filter,
    component?.pinned_posts?.split(",") || [],
    component?.shoppable_status_filter || "all",
    "default",
    "default",
    true,
    contentDateWindow
  );
  const filterMode = normalizeFilterForType(
    component?.shoppable_content_filter,
    "REELS"
  );

  // Process pages data similar to ReelsDataLoader pattern
  // Flatten pages array: each page is { data: [...], total_count: ..., originalDataLength: ... }
  // Extract data arrays from each page and flatten them
  const processedData = useMemo(() => {
    if (!ReelsData?.pages || !Array.isArray(ReelsData.pages)) return [];

    // Flatten pages: extract data array from each page object
    const flattened = ReelsData.pages
      .flatMap((page: any) => page?.data || [])
      .filter((element: any) => element !== undefined);

    return flattened.filter((item: any) => {
      const entryType = (item?.type || "").toUpperCase();
      if (filterMode === "reels") {
        return entryType === "REELS";
      }
      if (filterMode === "post") {
        return entryType === "FEED" || entryType === "POST";
      }
      // Show "All" includes stories; Posts & Reels ("both") does not.
      if (filterMode === "all") {
        return (
          entryType === "REELS" ||
          entryType === "FEED" ||
          entryType === "POST" ||
          entryType === "STORY" ||
          entryType === "HIGHLIGHTS"
        );
      }
      return (
        entryType === "REELS" ||
        entryType === "FEED" ||
        entryType === "POST"
      );
    });
  }, [ReelsData?.pages, filterMode]);

  const domLoaded = useDomLoaded();

  useEffect(() => {
    const checkEmptyStatus = async () => {
      if (!ReelsDataLoading) {
        if (!processedData || processedData.length === 0 || total_count === 0) {
          if (!showEmptyInEditor) {
            await updateIsEmptyStatus(title, user_id, component.id,from);
          }
        } else {
          await handleFirstImage(
            title,
            component?.id,
            processedData[0],
            user_id,
            from
          );
        }
      }
    };
    checkEmptyStatus();
  }, [
    ReelsDataLoading,
    processedData,
    total_count,
    title,
    user_id,
    component?.id,
  ]);

  return (
    <>
      {ReelsDataLoading || !domLoaded ? (<StayFeedLoadingPlaceholder
          designTokens={designTokens}
          from={from}
          component={component}
          templateGridLoaderProps={templateGridLoaderProps}
        />) : (
        <>
          {processedData && processedData.length > 0 && total_count > 0 ? (
            <PostTemplate
              processedData={processedData}
              total_count={total_count}
              component={component}
              user_id={user_id}
              username={username}
              handleRedirect={handleRedirect}
              brand_color={brand_color}
              title={title}
              from={from}
              type={type}
              isLinks={isLinks}
              itemsPerPage={itemsPerPage}
              designTokens={designTokens}
              pinnedPostIds={component?.pinned_posts?.split(",") || []}
            />
          ) : showEmptyInEditor ? (
            <FeedSectionEmptyState
              title={title}
              designTokens={designTokens}
              message={websiteEditorFeedEmptyMessage("platform", { platform: "Reels" })}
            />
          ) : (
            ""
          )}
        </>
      )}
    </>
  );
};

export default ReelsData;
